// ── BCHNostr — Creator Support goal widget (presentational) ───────
//
// The actual widget UI, extracted so it can be rendered TWO ways with
// identical output:
//
//   1. Standalone inside an <iframe> on any external site — see
//      pages/EmbedWidgetPage.tsx (the real, copy/paste embed).
//   2. Inline in the embed builder's "Live preview" — see
//      components/EmbedBuilderModal.tsx.
//
// Rendering the preview inline (rather than through a nested iframe that has
// to boot a second copy of the whole app) means the builder's live preview
// always paints — it never hangs on a spinner inside the Shakespeare preview
// environment or anywhere else the SPA fallback / service worker behaves
// differently for nested navigations.
//
// Everything here is self-contained inline styles (no dependency on the app's
// .dark class or Tailwind chrome) so the widget looks identical regardless of
// the host page. The theme comes from the `theme` prop.

import { forwardRef, type CSSProperties } from 'react';
import type { CreatorGoalData } from '../lib/creatorSupport';
import type { WidgetType, WidgetTheme } from '../lib/embed';

// ── Theme palettes ────────────────────────────────────────────────
export interface Palette {
  bg: string; card: string; border: string; text: string; muted: string;
  track: string; primary: string; primaryText: string; shadow: string;
}
export const LIGHT: Palette = {
  bg: 'transparent', card: '#ffffff', border: '#e5e7eb', text: '#0f172a',
  muted: '#64748b', track: '#e5e7eb', primary: '#0ac18e', primaryText: '#ffffff',
  shadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
};
export const DARK: Palette = {
  bg: 'transparent', card: '#0f172a', border: '#1e293b', text: '#f1f5f9',
  muted: '#94a3b8', track: '#1e293b', primary: '#0ac18e', primaryText: '#04231b',
  shadow: '0 1px 3px rgba(0,0,0,0.4)',
};

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

function fmtUSD(sats: number, price: number | null): string | null {
  if (!price || price <= 0) return null;
  const usd = (sats / 1e8) * price;
  if (usd < 0.01 && usd > 0) return '<$0.01';
  return '$' + usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtAmount(sats: number, price: number | null): string {
  return fmtUSD(sats, price) || `${sats.toLocaleString()} sats`;
}

// ── Small pieces ──────────────────────────────────────────────────

function BchGlyph({ color }: { color: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill={color} opacity="0.15" />
      <path
        d="M10 6.5h3.2c1.7 0 2.8 1 2.8 2.5 0 1-.5 1.7-1.3 2 .9.3 1.5 1.1 1.5 2.2 0 1.6-1.2 2.8-3 2.8H10V6.5Zm1.7 3.9h1.2c.7 0 1.1-.4 1.1-1s-.4-1-1.1-1h-1.2v2Zm0 3.9h1.4c.8 0 1.2-.4 1.2-1.1 0-.6-.5-1-1.2-1h-1.4v2.1Z"
        fill={color}
      />
      <rect x="11" y="4.8" width="1.1" height="2.4" rx="0.5" fill={color} />
      <rect x="11" y="16.8" width="1.1" height="2.4" rx="0.5" fill={color} />
    </svg>
  );
}

export function Fallback({ pal, text, link }: { pal: Palette; text: string; link?: string }) {
  return (
    <div style={{
      background: pal.card, border: `1px solid ${pal.border}`, borderRadius: 14,
      padding: 18, textAlign: 'center', boxShadow: pal.shadow,
    }}>
      <div style={{ fontSize: 13, color: pal.muted, marginBottom: link ? 10 : 0 }}>{text}</div>
      {link && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 13, fontWeight: 700, color: pal.primary, textDecoration: 'none' }}
        >
          Support on BCHNostr →
        </a>
      )}
    </div>
  );
}

// ── The widget ────────────────────────────────────────────────────

export interface GoalWidgetProps {
  type: WidgetType;
  theme: WidgetTheme;
  loading: boolean;
  found: boolean;
  data: CreatorGoalData | null;
  raisedSats: number;
  targetSats: number;
  targetUsd: number;
  pct: number;
  backerCount: number;
  price: number | null;
  /** The support-page URL the Donate button links to. */
  donateUrl: string;
  /** Whether a valid creator pubkey was resolved. */
  hasCreator: boolean;
  /** Fired when the donate button is clicked (analytics). Optional. */
  onDonateClick?: () => void;
}

/**
 * Pure, presentational goal widget. Takes fully-resolved goal data as props so
 * it has no data-fetching of its own — the standalone page and the builder
 * preview both feed it the same numbers from `useGoalEmbedData`.
 */
export const GoalWidget = forwardRef<HTMLDivElement, GoalWidgetProps>(function GoalWidget(
  {
    type, theme, loading, found, data, raisedSats, targetSats, targetUsd, pct,
    backerCount, price, donateUrl, hasCreator, onDonateClick,
  },
  ref,
) {
  const pal = theme === 'dark' ? DARK : LIGHT;

  const shell: CSSProperties = {
    fontFamily: FONT, background: pal.bg, color: pal.text,
    padding: 4, boxSizing: 'border-box', width: '100%',
  };

  const ProgressBar = ({ height = 8 }: { height?: number }) => (
    <div style={{ width: '100%', height, background: pal.track, borderRadius: 999, overflow: 'hidden' }}>
      <div
        style={{
          width: `${pct}%`, height: '100%', borderRadius: 999,
          background: `linear-gradient(90deg, ${pal.primary}, #16d19c)`,
          transition: 'width 0.6s ease',
        }}
      />
    </div>
  );

  const DonateButton = ({ full }: { full?: boolean }) => (
    <a
      href={donateUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onDonateClick}
      style={{
        width: full ? '100%' : 'auto', display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', gap: 6, padding: '10px 16px', borderRadius: 10,
        border: 'none', cursor: 'pointer', fontFamily: FONT, fontWeight: 700,
        fontSize: 14, background: pal.primary, color: pal.primaryText,
        boxShadow: pal.shadow, lineHeight: 1.2, textDecoration: 'none',
        boxSizing: 'border-box',
      }}
    >
      <BchGlyph color={pal.primaryText} />
      Donate with BCH
    </a>
  );

  const Branding = (
    <a
      href="https://bchnostr.com"
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        fontSize: 10, color: pal.muted, textDecoration: 'none', marginTop: 8,
      }}
    >
      <span style={{ opacity: 0.7 }}>Powered by</span>
      <span style={{ fontWeight: 700, color: pal.primary }}>BCHNostr</span>
    </a>
  );

  // ── States ──
  if (!hasCreator) {
    return <div ref={ref} style={shell}><Fallback pal={pal} text="No creator specified." /></div>;
  }

  if (loading) {
    return (
      <div ref={ref} style={shell}>
        <div style={{
          background: pal.card, border: `1px solid ${pal.border}`, borderRadius: 14,
          padding: 20, textAlign: 'center', color: pal.muted, fontSize: 13, boxShadow: pal.shadow,
        }}>
          Loading…
        </div>
      </div>
    );
  }

  if (!found || !data) {
    return (
      <div ref={ref} style={shell}>
        <Fallback pal={pal} text="This creator has no active goal." link={donateUrl} />
      </div>
    );
  }

  const goalDisplay = targetSats > 0 ? fmtAmount(targetSats, price) : `$${targetUsd}`;

  // ── Donate Button widget ──
  if (type === 'button') {
    return (
      <div ref={ref} style={{ ...shell, textAlign: 'center' }}>
        <DonateButton full />
        {Branding}
      </div>
    );
  }

  // ── Progress Bar widget ──
  if (type === 'progress') {
    return (
      <div ref={ref} style={shell}>
        <div style={{
          background: pal.card, border: `1px solid ${pal.border}`, borderRadius: 12,
          padding: 12, boxShadow: pal.shadow,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: pal.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {data.title}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: pal.primary, flexShrink: 0 }}>{pct}%</span>
          </div>
          <ProgressBar />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: pal.muted }}>
            <span><strong style={{ color: pal.text }}>{fmtAmount(raisedSats, price)}</strong> raised</span>
            <span>of {goalDisplay}</span>
          </div>
        </div>
        {Branding}
      </div>
    );
  }

  // ── Goal Card widget (default) ──
  const hasImage = !!data.image && /^https?:\/\//i.test(data.image);
  return (
    <div ref={ref} style={shell}>
      <div style={{
        background: pal.card, border: `1px solid ${pal.border}`, borderRadius: 16,
        overflow: 'hidden', boxShadow: pal.shadow,
      }}>
        {hasImage && (
          <div style={{ width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', background: pal.track }}>
            <img
              src={data.image}
              alt={data.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
        )}
        <div style={{ padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: pal.primary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Creator Goal
          </div>
          <h3 style={{
            margin: 0, fontSize: 15, fontWeight: 800, color: pal.text, lineHeight: 1.3,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {data.title}
          </h3>

          <div style={{ marginTop: 12 }}>
            <ProgressBar />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: pal.primary }}>{fmtAmount(raisedSats, price)}</div>
                <div style={{ fontSize: 11, color: pal.muted }}>raised of {goalDisplay}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: pal.text }}>{pct}%</div>
                {backerCount > 0 && (
                  <div style={{ fontSize: 11, color: pal.muted }}>
                    {backerCount} supporter{backerCount !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <DonateButton full />
          </div>
        </div>
      </div>
      {Branding}
    </div>
  );
});
